import _ from 'underscore';
import Backbone from 'backbone';
import PageApp from 'pageApp';
import Cookie from '@component/common/src/util/Cookie';
import ExperimentationSDK from '@component/experiment/src/js/ExperimentationSDK';
import ObjectUtil from '@core/util/object';
import { getProperty } from '../util/PropertyGetter';
import {
  format,
  BOOL,
  CURRENCY,
  ARRAY,
} from '../util/TealiumFormattingUtil';
import {
  Actions,
  AddToBag,
  Cookies,
  ApplicationMessages,
  Timeouts,
} from '../util/Constants';
import AddToBagModel from './AddToBagModel';
import Properties from '../util/Properties';
import {
  inStoreCurbsidePickupMessage,
  checkCutOffTime,
  checkPickupEligibility,
  bossPickupMessagging,
  getEventMessaging,
} from '../../helpers/pickupMessaging';

export default class AddToBagController {
  constructor(options) {
    Object.assign(this, options);
    this.responsiveBagAddEnabled = Properties.get('responsiveBagAddEnabled');
  }

  validateIfBigTicket() {
    const isValid = !this.productModel.get('product').detail.flags.bigTicketItem || Cookie.get(Cookies.bigTicketZipCode, Cookies.miscGCs);
    if (!isValid) {
      PageApp.channel.trigger(`${Actions.product.ctaNotification.error}:${this.eventId}`, ApplicationMessages.invalidZipCode);
    }
    return isValid;
  }

  fireAddToBagCall() {
    if (!this.validateIfBigTicket()) {
      return;
    }

    const addToBagModel = new AddToBagModel({}, {
      productModel: this.productModel,
      source: this.source,
    });

    this.toggleLoading(true);

    if (this.responsiveBagAddEnabled) {
      const attrs = {
        item: addToBagModel.toJSON(),
      };

      addToBagModel.save(attrs, {
        attrs,
        success: this.handleSuccess.bind(this),
        error: this.handleError.bind(this),
        timeout: Timeouts.async,
      });
    } else {
      Backbone.emulateJSON = true;
      addToBagModel.save({}, {
        data: addToBagModel.toJSON(),
        success: this.handleSuccess.bind(this),
        error: this.handleError.bind(this),
        timeout: Timeouts.async,
      });
      Backbone.emulateJSON = false;
    }
  }

  toggleLoading(toggle) {
    const { showLoader, hideLoader } = Actions.product.ctaButton;
    const action = toggle ? showLoader : hideLoader;
    const eventName = `${action}:${this.eventId}`;

    PageApp.channel.trigger(eventName, this.$currentTarget);
  }

  handleSuccess(model, response, options) {
    let bagItem;

    this.toggleLoading(false);

    if (_.isEmpty(response)) {
      this.handleError(model, response, options);
      return;
    }

    if (this.responsiveBagAddEnabled) {
      [bagItem] = getProperty(response, 'bag.sections.bagItems.items', []);
    } else if (Array.isArray(response.bagItems) && response.bagItems.length > 0) {
      [bagItem] = response.bagItems;
    }

    if (!this.checkForErrors(model, response, options, bagItem)) {
      return;
    }

    if (!AddToBagController.setCookies(response)) {
      return;
    }

    if (bagItem) {
      AddToBagController.triggerTealiumLinkTag(model, response, bagItem);
    }

    PageApp.channel.trigger(Actions.product.bag.success, {
      requestData: options.data,
      responseData: response,
      productModel: this.productModel,
    });
  }

  static getAvailabilityTypes(bagItem) {
    let isAvblForShip;
    let isAvblForBOSS;
    let isAvblForBOPS;
    const responsiveBagAddEnabled = Properties.get('responsiveBagAddEnabled');
    if (bagItem) {
      if (responsiveBagAddEnabled) {
        isAvblForShip = bagItem.flags.shippingAvailability;
        isAvblForBOSS = bagItem.flags.shipToStoreAvailability;
        isAvblForBOPS = bagItem.flags.storeAvailability;
      } else {
        isAvblForShip = bagItem.shippingAvailability;
        isAvblForBOSS = bagItem.shiptoStoreAvailability;
        isAvblForBOPS = bagItem.storeAvailability;
      }

      if (isAvblForBOSS && !isAvblForShip) {
        return ['ship-n:bops-n:boss-y'];
      } else if (isAvblForBOPS && !isAvblForShip) {
        return ['ship-n:bops-y:boss-n'];
      } else if (isAvblForShip && (!isAvblForBOPS && !isAvblForBOSS)) {
        return ['ship-y:bops-n:boss-n'];
      } else if (isAvblForShip && isAvblForBOPS) {
        return ['ship-y:bops-y:boss-n'];
      } else if (isAvblForShip && isAvblForBOSS) {
        return ['ship-y:bops-n:boss-y'];
      }
    }

    return [''];
  }

  static getAvailabilitySelect(bagItem) {
    const responsiveBagAddEnabled = Properties.get('responsiveBagAddEnabled');

    if (bagItem) {
      if (responsiveBagAddEnabled) {
        const isAvblForShip = bagItem.flags.shippingAvailability;
        const isAvblForBOSS = bagItem.flags.shipToStoreSelected;
        const isAvblForBOPS = bagItem.flags.pickUpFromStore;

        if (isAvblForBOPS) {
          return ['bops'];
        } else if (isAvblForBOSS) {
          return ['boss'];
        } else if (isAvblForShip) {
          return ['ship'];
        }
      } else if (bagItem.pickupFromStore) {
        return ['bops'];
      } else if (bagItem.shiptoStoreFlag) {
        return ['boss'];
      } else if (bagItem.shippingAvailability) {
        return ['ship'];
      }
    }
    return [''];
  }

  static triggerTealiumLinkTag(model, response, bagItem) {
    const protectionPlans = getProperty(model, 'productModel.attributes.viewState.detail.selectedUpcItem.protectionPlans', false);
    const planId = getProperty(model, 'productModel.attributes.viewState.detail.selectedPlanId', false);
    const selectedPlan = protectionPlans && protectionPlans.find(item => item.planId === `${planId}`);
    const planName = selectedPlan ? selectedPlan.planName : '';
    // const consolidatedProductComplex = getProperty(model, 'productModel.attributes.viewState.detail.flags.consolidatedProductComplex', false);
    const isTrueFitEligible = getProperty(model, 'productModel.attributes.viewState.detail.flags.isTrueFitEligible', false);
    // const attributeInteractedCount = model.productModel.get('viewState').detail.tealiumInteractedAttributeCount;
    let bigTicketItem;
    let productId;
    let productName;
    let upcNumber;
    let color;
    let size;
    let quantity;
    let salePrice;
    let originalPrice;
    let totalPrice;
    let colorWayPrice;
    let categoryId;
    let typeName;
    let consolidatedProducts;
    const responsiveBagAddEnabled = Properties.get('responsiveBagAddEnabled');

    if (bagItem) {
      if (responsiveBagAddEnabled) {
        bigTicketItem = bagItem.flags && bagItem.flags.bigTicketItem;
        ({ productId } = bagItem);
        productName = bagItem.product && bagItem.product.detail.name;
        ({ upcNumber } = bagItem);
        const { product: { traits: { colors, sizes } = {} } = {} } = bagItem;
        color = colors && colors[0] ? colors[0] : null;
        size = sizes && sizes[0] ? sizes[0] : null;

        ({ quantity } = bagItem);

        if (response.bag.meta && response.bag.meta.analytics) {
          salePrice = response.bag.meta.analytics.data.product_price;
          originalPrice = response.bag.meta.analytics.data.product_original_price;
          totalPrice = response.bag.meta.analytics.data.product_sub_total;
        }

        categoryId = bagItem.product && bagItem.product.detail.defaultCategoryId;
        typeName = bagItem.productCategoryName;
      } else {
        ({ bigTicketItem } = bagItem);
        productId = bagItem.productID;
        ({ productName } = bagItem);
        ({ upcNumber } = bagItem);
        ({ color } = bagItem);
        ({ size } = bagItem);
        ({ quantity } = bagItem);
        ({ salePrice } = bagItem);
        originalPrice = bagItem.origPrice;
        totalPrice = bagItem.total;
        categoryId = bagItem.categoryID;
        ({ typeName } = bagItem);
      }
      colorWayPrice = !_.isEmpty(bagItem.colorwayPrice && bagItem.colorwayPrice.priceTypeText) ? [bagItem.colorwayPrice.priceTypeText.toString()] : [''];
    }

    const event = {
      event_name: 'add to bag',
      is_big_ticket: format(bigTicketItem, BOOL),
      product_availability: format(AddToBagController.getAvailabilityTypes(bagItem), ARRAY),
      product_availability_select: format(AddToBagController.getAvailabilitySelect(bagItem), ARRAY),
      product_id: format(productId),
      product_name: format(productName),
      product_upc: format(upcNumber),
      product_color: format(color),
      product_size: format(size),
      product_quantity: format(quantity),
      product_price: format(salePrice, CURRENCY),
      product_original_price: format(originalPrice, CURRENCY),
      product_pricing_state: colorWayPrice,
      product_sub_total: format(totalPrice, CURRENCY),
      product_type: consolidatedProducts ? ['master'] : format(model.getPageType()),
      t_category_id: format(categoryId),
      product_protection_plan: format(planName),
      t_category_name: format(typeName),
      consolidated_product: format(consolidatedProducts, BOOL),
      pdp_outfitting: [$('#pdp-overlay-modal-id').hasClass('visible').toString()],
    };

    const holidayKS = getProperty(model, 'productModel.attributes.properties.globalHolidayMessageEnabled', false);
    if (holidayKS) {
      const holidayMessageEligible = getProperty(model, 'productModel.attributes.viewState.detail.selectedUpcItem.holidayMessageEligible', false);
      if (holidayMessageEligible) {
        const holidayShipMessage = getProperty(model, 'productModel.attributes.viewState.detail.selectedUpcItem.holidayMessages.beforeStandardCutoff', false);
        const holidayPickupMessage = getProperty(model, 'productModel.attributes.viewState.detail.selectedUpcItem.availability.holidayMessages.beforeBossCutoff');
        event.holiday_eligibility = format(`${holidayShipMessage ? 'Shipping:BH' : 'Shipping:AH'}|${holidayPickupMessage ? 'Pickup:BH' : `${(holidayPickupMessage !== undefined) ? 'Pickup:AH' : 'Pickup:NA'}`}`);
      } else {
        event.holiday_eligibility = format(`${'Shipping:NA'} | ${'Pickup:NA'}`);
      }
    }

    const curbsidePickupEnabled = getProperty(model, 'productModel.attributes.properties.curbsidePickupEnabled');
    const inStoreCurbsidePickupEnabled = getProperty(model, 'productModel.attributes.properties.inStoreCurbsidePickupEnabled');
    const productModel = getProperty(model, 'productModel');
    if ((curbsidePickupEnabled || inStoreCurbsidePickupEnabled) && ObjectUtil.hasChildProperty(productModel, 'attributes.product.store')) {
      bossPickupMessagging(productModel);
      const checkPickupEligibilityFlag = checkPickupEligibility(productModel);
      const checkCutOffTimeFlag = checkCutOffTime(productModel);
      inStoreCurbsidePickupMessage(productModel, checkCutOffTimeFlag, checkPickupEligibilityFlag);
      const pickupEligibility = getEventMessaging(productModel);
      event.pickup_eligibility = format(pickupEligibility);
    }

    if (isTrueFitEligible) {
      const { trueFitRec } = model.productModel.get('viewState');
      if (trueFitRec) {
        event.is_true_fit_size = trueFitRec === size ? ['true'] : ['false'];
      } else {
        event.is_true_fit_size = [''];
      }
    }

    PageApp.channel.trigger(Actions.tealium.linkTag, event);
  }

  static triggerSuppressATBLinkTag() {
    const expRecipes = ['403-21', '403-20', '403-11'];

    return new Promise((resolve) => {
      ExperimentationSDK.getSelectedRecipesForExperiment(expRecipes).then((selectedRecipe) => {
        if (selectedRecipe && selectedRecipe.length > 0) {
          const tagData = {
            event_name: 'experiment',
            experimentation_ids: selectedRecipe[0],
          };
          PageApp.channel.trigger(Actions.tealium.linkTag, tagData);
        }
        resolve();
      }, () => resolve());
    });
  }


  checkForErrors(model, response, options, bagItem) {
    const bagMetaMessageError = getProperty(response, 'bag.meta.messages.error[0]'); // For Bag Xapi Meta level error msg
    const bagItemMessageError = getProperty(bagItem, 'messages.error[0]'); // For Bag Xapi item level error msg
    const bagAppItemError = getProperty(bagItem, 'errorCodes[0]'); // For Legacy Bag App error msg

    const errorCode = this.responsiveBagAddEnabled ?
      bagMetaMessageError || bagItemMessageError : bagAppItemError;

    const localOptions = options || {};

    if (errorCode) {
      localOptions.errorMessage = errorCode;
      this.handleError(model, response, localOptions);
      return false;
    }
    return true;
  }

  static setCookies(response) {
    const responsiveBagAddEnabled = Properties.get('responsiveBagAddEnabled');
    const expires = new Date(new Date().getTime() + Cookies.thirthyDaysInMS);

    const userId = responsiveBagAddEnabled ? response.bag.meta.userId : response.userId;
    const { machineId } = response;
    const bagGuid = responsiveBagAddEnabled ? response.bag.meta.bagGuid : response.bagGuid;

    AddToBagController.setCookie(Cookies.userId, userId, expires);
    if (!Cookie.get(Cookies.userId) && userId) {
      PageApp.channel.trigger(Actions.page.redirect, AddToBag.noSessionPage);
      return false;
    }

    AddToBagController.setCookie(Cookies.machineId, machineId, expires);
    AddToBagController.setCookie(Cookies.bagGuid, bagGuid, expires);
    if (responsiveBagAddEnabled && response.bag.meta.dca) {
      const expiryDate = new Date(new Date().getTime() + Cookies.oneDayInMS);
      AddToBagController.setCookie(Cookies.dca, response.bag.meta.dca, expiryDate);
    }
    return true;
  }


  static setCookie(cookieName, cookieValue, expires) {
    Cookie.set(cookieName, cookieValue, undefined, {
      expires,
    });
  }

  handleError(model, response, options) {
    this.toggleLoading(false);
    const message = options.errorMessage || ApplicationMessages.technicalIssue;
    PageApp.channel.trigger(`${Actions.product.ctaNotification.error}:${this.eventId}`, message);
  }
}



// WEBPACK FOOTER //
// /Users/m802941/WebstormProjects/2020/campaigns/shop-the-look-pdp/node_modules/@page/digital-product-ui-mcom/src/js/add-to-bag/AddToBagController.js